import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { TOK, SERIF, SANS } from "../soulumoCreate/shared";

export const API = process.env.REACT_APP_BACKEND_URL;
export const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
export const ADELAIDA_IMG = "/coaches/adelaida_calm.webp";

// One shared audio element: Adelaida's pre-recorded lines and the finished example clip.
export const useVoice = () => {
  const el = useRef(null);
  if (!el.current && typeof Audio !== "undefined") el.current = new Audio();
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  useEffect(() => {
    const a = el.current;
    const on = () => setPlaying(true);
    const off = () => setPlaying(false);
    const tick = () => setTime(a.currentTime);
    a.addEventListener("play", on); a.addEventListener("pause", off); a.addEventListener("ended", off); a.addEventListener("timeupdate", tick);
    return () => { a.pause(); a.removeEventListener("play", on); a.removeEventListener("pause", off); a.removeEventListener("ended", off); a.removeEventListener("timeupdate", tick); };
  }, []);
  const play = (url, onEnd) => {
    const a = el.current;
    if (!url) { onEnd?.(); return; }
    a.pause();
    a.src = url.startsWith("http") ? url : `${API}${url}`;
    a.onended = () => onEnd?.();
    a.play().catch(() => onEnd?.());
  };
  return { el: el.current, play, playing, time, stop: () => el.current?.pause() };
};

// Frosted-glass play button: translucent gold, soft inner light, breathing ring while playing.
export const GlassPlay = ({ playing, onClick, testId, Icon }) => (
  <button type="button" onClick={onClick} data-testid={testId} aria-label={playing ? "Pause" : "Play"}
    className="relative mx-auto grid place-items-center w-28 h-28 rounded-full transition-transform duration-300 active:scale-95 hover:scale-[1.03]"
    style={{
      background: "radial-gradient(120% 120% at 30% 20%, rgba(240,216,160,0.62), rgba(215,187,122,0.26) 50%, rgba(120,92,40,0.28))",
      backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
      border: "1px solid rgba(245,226,180,0.6)",
      boxShadow: "inset 0 1px 1px rgba(255,246,222,0.7), inset 0 -12px 22px rgba(90,64,20,0.28), 0 0 0 8px rgba(215,187,122,0.08), 0 18px 44px -10px rgba(215,187,122,0.55)",
    }}>
    <span className="pointer-events-none absolute left-1/2 top-2 h-7 w-16 -translate-x-1/2 rounded-full" style={{ background: "linear-gradient(180deg, rgba(255,250,235,0.32), transparent)" }} />
    {playing && <motion.span className="pointer-events-none absolute inset-0 rounded-full" style={{ border: "1px solid rgba(215,187,122,0.6)" }}
      animate={{ scale: [1, 1.35], opacity: [0.7, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeOut" }} />}
    <Icon size={34} style={{ color: "#f8ecd0", filter: "drop-shadow(0 2px 6px rgba(13,31,58,0.5))" }} className={playing ? "" : "ml-1"} />
  </button>
);

export const Bubble = ({ from, children, testId, delay = 0 }) => {
  const her = from === "adelaida";
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay }}
      className={`flex items-end gap-2 ${her ? "" : "flex-row-reverse"}`} data-testid={testId}>
      {her ? <img src={ADELAIDA_IMG} alt="Adelaida" className="w-8 h-8 rounded-full object-cover object-top shrink-0" />
        : <span className="w-8 h-8 rounded-full grid place-items-center text-xs shrink-0" style={{ background: "rgba(215,187,122,0.18)", color: TOK.gold, fontFamily: SANS }}>A</span>}
      <div className="max-w-[80%] rounded-2xl px-4 py-3 text-[0.98rem] leading-relaxed"
        style={her ? { background: "rgba(245,236,217,0.07)", color: TOK.cream, fontFamily: SERIF, fontSize: "1.08rem" }
          : { background: "rgba(215,187,122,0.16)", color: TOK.cream, fontFamily: SANS }}>
        {children}
      </div>
    </motion.div>
  );
};

export const StepTitle = ({ kicker, title }) => (
  <div className="mb-6">
    <p className="text-[0.62rem] uppercase tracking-[0.32em]" style={{ color: TOK.gold, fontFamily: SANS }}>{kicker}</p>
    <h1 className="mt-2 text-3xl sm:text-4xl font-light leading-tight" style={{ fontFamily: SERIF, color: TOK.cream }}>{title}</h1>
  </div>
);

export const NextBtn = ({ onClick, children = "Next", testId, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className="mt-8 w-full rounded-full py-4 text-[0.72rem] uppercase tracking-[0.26em] transition-[opacity,transform] duration-200 active:scale-[0.98] disabled:opacity-40"
    style={{ background: TOK.gold, color: "#0d1f3a", fontFamily: SANS }}>{children}</button>
);

export const Dots = ({ step, total }) => (
  <div className="flex gap-1.5" data-testid="demo-progress">
    {Array.from({ length: total }).map((_, i) => (
      <span key={i} className="h-1 rounded-full transition-[width,background-color] duration-300"
        style={{ width: i === step ? 22 : 8, background: i <= step ? TOK.gold : "rgba(245,236,217,0.18)" }} />
    ))}
  </div>
);
